import React, {Component} from 'react';
import {connect} from 'react-redux';
import memoize from 'memoize-one';

import {
    Dropdown,
    DropdownChangeEvent,
    DropdownProps
} from 'primereact/dropdown';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import User from '~/shared/src/model/User';

import {StoreState} from '~/frontend/src/store';
import {getStatusDisplayName} from '~/frontend/src/utilities/renderHelpers';
import {
    statusColoringRenderer,
    statusRenderer
} from '~/frontend/src/utilities/valueRenderers';


export type StatusDropDownMode = 'all' | 'settable' | 'onlyActiveAndAll';


const defaultProps = {

    /**
     * The database schema to filter status for.
     */
    schema: 'DATA' as Schema,

    /**
     * The mode to select status.
     */
    mode: 'all' as StatusDropDownMode,

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Should the field marked if dirty.
     */
    markDirty: false as boolean,

    /**
     * The label for the field.
     */
    label: '',

    /**
     * The CSS class name(s) to add to the component.
     */
    className: '',

    inputStyle: { width: '100%' } as React.CSSProperties
};

type DefaultProps = Readonly<typeof defaultProps>;

type Props = {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The selected status.
     */
    value : Status;

    /**
     * The value to determine dirty against.
     */
    originalValue? : Status;

    /**
     * Callback to invoke if value has changed.
     *
     * @param {Status} status
     */
    onChange : ( status : Status, originalEvent : any ) => void;

    style? : React.CSSProperties;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

export type ComponentProps = Omit<DropdownProps, 'onChange'> & Props & Partial<DefaultProps>;
type AllProps = Omit<DropdownProps, 'onChange'> & Props & DefaultProps & StateProps & { dispatch : any };


/**
 * Dropdown for status selection
 */
class StatusDropdown extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.getData = memoize( this.getData );

        this.onChange = this.onChange.bind( this );
    }

    /**
     * Get data for combo
     *
     * @param {Schema} schema
     * @param {StatusDropDownMode} mode
     * @param {boolean} rawMode
     * @param {boolean} showAdditionalRawNames
     *
     * @return {{ value : string; label : string }[]}
     */
    getData( schema : Schema, mode : StatusDropDownMode, rawMode : boolean, showAdditionalRawNames : boolean ) : { value : string; label : string }[] {
        const retVal : { value : string; label : string }[] = [];

        if ( mode === 'onlyActiveAndAll' ) {
            retVal.push( {
                value: 'active',
                label: getStatusDisplayName( 'active', schema, rawMode, showAdditionalRawNames )
            } );
            retVal.push( {
                value: 'all-non-history',
                label: __( 'YN.base.status.allNonHistory' )
            } );
            retVal.push( {
                value: 'all',
                label: __( 'YN.base.status.all' )
            } );
        }

        else {
            const data              = metaModel[ schema ].getStatuses(),
                  notSettableStatus = [
                      metaModel[ schema ].getStatus( 'history' )?.id,
                      metaModel[ schema ].getStatus( 'deleted' )?.id
                  ];

            // sort
            data.sort( ( val1, val2 ) => {
                if ( val1.description > val2.description ) {
                    return 1;
                }
                else if ( val1.description < val2.description ) {
                    return -1;
                }
                else {
                    return 0;
                }
            } );

            for ( const status of data ) {
                if ( mode === 'all' || !notSettableStatus.includes( status.id ) ) {
                    retVal.push( {
                        value: status.name,
                        label: getStatusDisplayName( status.id, schema, rawMode, showAdditionalRawNames )
                    } );
                }
            }
        }

        return retVal;
    }

    /**
     * Handler for Dropdown change event
     *
     * @private
     */
    onChange( event : DropdownChangeEvent ) {
        if ( event.value ) {
            this.props.onChange( event.value as Status, event );
        }
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  value,
                  originalValue,
                  schema,
                  mode,
                  rawMode,
                  showAdditionalRawNames,
                  markDirty,
                  label,
                  className,
                  style,
                  inputStyle,
                  // onChange is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  onChange,
                  // user is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  user,
                  // dispatch is listed here to be excluded from dropdownProps
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  dispatch,
                  ...dropdownProps
              }                    = this.props,
              data                 = this.getData( schema, mode, rawMode, showAdditionalRawNames ),
              dropdownClassNames   = [ 'yn-inputgroup' ],
              // template
              dataTemplate         = ( option : any ) => {
                  let retVal : string | JSX.Element;

                  if ( option.value !== 'all' && option.value !== 'all-non-history' ) {
                      retVal = statusRenderer( option.value, schema, rawMode, showAdditionalRawNames );
                      retVal = statusColoringRenderer( retVal, option.value, schema );
                  }
                  else {
                      retVal = option.label;
                  }

                  return retVal;
              },
              selectedDataTemplate = ( option : any, props : any ) => {
                  let retVal : string | JSX.Element = <>{props.placeholder ?? '&nbsp;'}</>;

                  if ( option ) {
                      if ( option.value !== 'all' && option.value !== 'all-non-history' ) {
                          retVal = statusRenderer( option.value, schema, rawMode, showAdditionalRawNames );
                          retVal = statusColoringRenderer( retVal, option.value, schema );
                      }
                      else {
                          retVal = option.label;
                      }
                      retVal = <div className="yn-multiselect-selected-item">{retVal}</div>;
                  }

                  return retVal;
              };

        if ( !label ) {
            dropdownClassNames.push( className );
        }
        if ( markDirty && value !== originalValue ) {
            dropdownClassNames.push( 'yn-dirty-border' );
        }

        return <div className={className + ' field'} style={style}>
            {label
                ? <label
                    htmlFor={`${uid}_statusDropdown_i`}
                    className="block"
                >
                    {label}
                </label>
                : ''
            }
            <Dropdown
                {...dropdownProps}
                id={`${uid}_statusDropdown`}
                inputId={`${uid}_statusDropdown_i`}
                value={value}
                onChange={this.onChange}
                options={data}
                optionLabel="label"
                className={dropdownClassNames.join( ' ' )}
                placeholder={__( 'YN.base.finder.status' )}
                valueTemplate={selectedDataTemplate}
                itemTemplate={dataTemplate}
                style={inputStyle}
            />
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

// typing of the connect function makes working that the action props are defined but not required when using the component
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect<Partial<ComponentProps>, {}, ComponentProps, StoreState>( mapStateToProps )( StatusDropdown );
